import { z } from 'zod';

/**
 * What a Zevik pack may carry from each external framework
 * (docs/DECISIONS.md ZD-032, ZD-035).
 *
 * **This is a control, not documentation.** External frameworks' terms differ
 * materially — one of them is copyrighted and sold, two of them forbid the very
 * thing we would be doing — and a licence constraint recorded only in prose is
 * a constraint that expires with the session that wrote it. So the terms are
 * data, `FrameworkMappingRowSchema` refuses a row carrying more than its
 * framework allows, and `FRAMEWORK_IDS` is a closed enum so a framework with no
 * policy entry is unrepresentable rather than merely undiscussed.
 *
 * **The rejected entries are here on purpose.** An omission reads as an
 * oversight and invites a future session to add the framework; a `rejected`
 * entry with its reason attached makes adopting one a decision somebody takes
 * deliberately.
 *
 * Nothing here is legal advice. It records the position QRS has taken, with the
 * licence it rests on, so that the position is reviewable.
 */

/**
 * How much of a framework's own text a mapping row may reproduce.
 *
 * The ladder is deliberately coarse. A finer one ("titles but only in
 * appendices") would be a judgement call at every call site, and the point of
 * this file is that no call site makes the judgement.
 */
export const FRAMEWORK_PERMITS = ['identifier', 'identifier_and_title', 'full_text'] as const;
export const FrameworkPermitsSchema = z.enum(FRAMEWORK_PERMITS);
export type FrameworkPermits = z.infer<typeof FrameworkPermitsSchema>;

/**
 * Whose identifier a mapping row's `control_id` is (ZD-035 §4.4).
 *
 * `permits` answers *how much of a framework's text may be carried*, and its
 * lowest rung is `identifier` — which **authorises** carrying the framework's
 * own identifier scheme. For Cyber Essentials nobody has cleared even that: the
 * requirements document is Crown Copyright with no confirmed open licence. The
 * catalogue therefore keys CE rows by a QRS-side machine key
 * (`ce.user_access_control`), not by CE's own `A6.x` numbering.
 *
 * That is a stricter posture than `permits: 'identifier'` describes, and the
 * gap is the kind that decays into a rationale nobody reads. So it is a second
 * field with a parse-time refusal behind it, rather than a sentence.
 */
export const IDENTIFIER_SOURCES = ['framework', 'qrs_machine_key'] as const;
export const IdentifierSourceSchema = z.enum(IDENTIFIER_SOURCES);
export type IdentifierSource = z.infer<typeof IdentifierSourceSchema>;

export const FRAMEWORK_IDS = [
  'nist_csf',
  'nist_800_53',
  'nist_800_171',
  'nist_ai_rmf',
  'nist_ai_600_1',
  'ncsc_caf',
  'cyber_essentials',
  'iso_27001',
  'iso_27002',
  'secure_controls_framework',
  'cis_controls',
] as const;
export const FrameworkIdSchema = z.enum(FRAMEWORK_IDS);
export type FrameworkId = z.infer<typeof FrameworkIdSchema>;

export interface FrameworkPolicy {
  /** How the framework is named to a human — in a report, a review sheet, an export. */
  display_name: string;
  status: 'permitted' | 'rejected';
  /**
   * What a mapping row may carry. `null` for a rejected framework: there is no
   * level at which it may be carried, which is a different statement from
   * "identifier only" and must not be expressible as one.
   */
  permits: FrameworkPermits | null;
  /**
   * Whose identifier scheme a row's `control_id` uses. `null` for a rejected
   * framework, for the same reason `permits` is.
   */
  identifier_source: IdentifierSource | null;
  /**
   * The edition in force, so a row pinned to a superseded one is reportable
   * (`supersededMappings`). Cyber Essentials revises annually, which is the
   * difference between visible drift and silent rot.
   *
   * `null` means **nobody has established it** — not "any version is current".
   * `supersededMappings` reports a row on such a framework as *undecidable*
   * rather than passing it, because the conservative direction on a licensing
   * and accuracy question is to say we do not know.
   */
  current_version: string | null;
  /**
   * The date somebody last confirmed `current_version` against the publisher,
   * `YYYY-MM-DD`, or `null` where nobody has.
   *
   * **Without this the first field decays into a lie.** A hardcoded "current"
   * that nobody re-checks makes the drift report say *nothing superseded*
   * forever, and say it with more apparent confidence as time passes. The
   * staleness of the check has to be as visible as the staleness it detects,
   * which is what `staleVersionChecks` reports.
   */
  current_version_confirmed_on: string | null;
  licence: string;
  source: string;
  /** Reproduced beside any mapping to this framework, where its licence requires it. */
  attribution: string | null;
  /** Why this status. Read by the review sheets and by whoever revisits the call. */
  rationale: string;
}

export const FRAMEWORK_POLICY: Record<FrameworkId, FrameworkPolicy> = {
  nist_csf: {
    display_name: 'NIST CSF 2.0',
    status: 'permitted',
    permits: 'full_text',
    identifier_source: 'framework',
    current_version: '2.0',
    current_version_confirmed_on: '2026-08-21',
    licence: 'US Government work — not subject to domestic copyright',
    source: 'https://www.nist.gov/cyberframework',
    attribution: null,
    rationale:
      'The primary mapping target. Free for commercial embedding, and published in machine-' +
      'readable form, so a mapping can be checked against the source rather than transcribed.',
  },
  nist_800_53: {
    display_name: 'NIST SP 800-53 Rev 5',
    status: 'permitted',
    permits: 'full_text',
    identifier_source: 'framework',
    current_version: null,
    current_version_confirmed_on: null,
    licence: 'US Government work — not subject to domestic copyright',
    source: 'https://csrc.nist.gov/pubs/sp/800/53/r5/upd1/final',
    attribution: null,
    rationale:
      'Same basis as CSF, at the granularity a technical report needs. **Permitted but not ' +
      'yet mapped:** the v1.0.0 catalogue carries no 800-53 rows, and nobody has confirmed ' +
      'which revision is current, so `current_version` is null rather than guessed.',
  },
  nist_800_171: {
    display_name: 'NIST SP 800-171',
    status: 'permitted',
    permits: 'full_text',
    identifier_source: 'framework',
    current_version: null,
    current_version_confirmed_on: null,
    licence: 'US Government work — not subject to domestic copyright',
    source: 'https://csrc.nist.gov/pubs/sp/800/171/r3/final',
    attribution: null,
    rationale:
      'Same basis. Relevant where a client is in a US federal supply chain. Permitted but not ' +
      'yet mapped, so no revision is claimed as current.',
  },
  nist_ai_rmf: {
    display_name: 'NIST AI Risk Management Framework 1.0',
    status: 'permitted',
    permits: 'full_text',
    identifier_source: 'framework',
    current_version: '1.0',
    current_version_confirmed_on: '2026-08-21',
    licence: 'US Government work — not subject to domestic copyright',
    source: 'https://www.nist.gov/itl/ai-risk-management-framework',
    attribution: null,
    rationale:
      'The AI-domain mapping target, on the same basis as CSF. Its subcategories are process ' +
      'outcomes rather than technical controls, which is why a purely technical QRS control ' +
      'maps to one at `advisory` and a process-shaped one at `partial` — the calibration is ' +
      'recorded in the catalogue beside the AI domain, not decided per row.',
  },
  nist_ai_600_1: {
    display_name: 'NIST AI 600-1 Generative AI Profile',
    status: 'permitted',
    permits: 'full_text',
    identifier_source: 'framework',
    current_version: 'Jul 2024',
    current_version_confirmed_on: '2026-08-21',
    licence: 'US Government work — not subject to domestic copyright',
    source: 'https://csrc.nist.gov/pubs/ai/100/1/final',
    attribution: null,
    rationale:
      'A companion profile to the AI RMF, carried on the same basis. Mapped under a **targeted ' +
      'charter** rather than exhaustively: a row exists only where a 600-1 suggested action ' +
      "materially upgrades a control's AI-framework fidelity, so the absence of a row here " +
      'carries no meaning — which is stated in the catalogue as `coverage: targeted` rather ' +
      'than left to a reader to infer.',
  },
  ncsc_caf: {
    display_name: 'NCSC Cyber Assessment Framework',
    status: 'permitted',
    permits: 'identifier_and_title',
    identifier_source: 'framework',
    current_version: 'v4.0',
    current_version_confirmed_on: '2026-08-21',
    licence: 'Open Government Licence v3.0',
    source: 'https://www.ncsc.gov.uk/collection/cyber-assessment-framework',
    attribution:
      'Contains public sector information licensed under the Open Government Licence v3.0.',
    rationale:
      'Permissive with attribution. The secondary target, for UK relevance — and because QRS ' +
      'is Cyber Essentials accredited, so CAF language is what a UK client already reads. The ' +
      'v1.0.0 catalogue maps v4.0, with every row authored against the v4.0 text.',
  },
  cyber_essentials: {
    display_name: 'Cyber Essentials',
    status: 'permitted',
    permits: 'identifier',
    identifier_source: 'qrs_machine_key',
    current_version: 'v3.3',
    current_version_confirmed_on: '2026-08-21',
    licence: 'Crown Copyright — terms unconfirmed, no confirmed open licence',
    source: 'https://www.ncsc.gov.uk/cyberessentials/overview',
    attribution: null,
    rationale:
      'The most restrictive posture available, because the licence question is open rather ' +
      'than answered: the Requirements for IT Infrastructure document is Crown Copyright and ' +
      'no open licence has been confirmed for it. So no CE text is carried at all — not a ' +
      "control title, and not CE's own `A6.x` identifiers. Rows are keyed by a QRS-side " +
      'machine key (`ce.user_access_control`), which is why `identifier_source` is ' +
      '`qrs_machine_key` and a row carrying a CE identifier is refused at the parse. CE ' +
      'matters despite this: it is the most widely held certificate among UK SMEs, and where ' +
      'it stops is a product output in its own right (`qrsart_ce.scope_clarification`). ' +
      '**Reopen when:** terms are confirmed with IASME or NCSC, at which point `permits` and ' +
      '`identifier_source` are both revisited together.',
  },
  iso_27001: {
    display_name: 'ISO/IEC 27001',
    status: 'permitted',
    permits: 'identifier',
    identifier_source: 'framework',
    current_version: '2022',
    current_version_confirmed_on: '2026-08-21',
    licence: 'Copyrighted, sold by ISO/IEC and national standards bodies',
    source: 'https://www.iso.org/standard/27001',
    attribution: null,
    rationale:
      'Clause identifiers only. The standard is sold, so its control text may not be embedded ' +
      'in a product — and every name shown beside an identifier is QRS-authored, which is why ' +
      '`control_name` is nullable rather than required (ZD-019: a field with no honest value ' +
      'manufactures an assertion).',
  },
  iso_27002: {
    display_name: 'ISO/IEC 27002',
    status: 'permitted',
    permits: 'identifier',
    identifier_source: 'framework',
    current_version: null,
    current_version_confirmed_on: null,
    licence: 'Copyrighted, sold by ISO/IEC and national standards bodies',
    source: 'https://www.iso.org/standard/75652.html',
    attribution: null,
    rationale:
      'Same basis as ISO 27001. Permitted but not mapped in the v1.0.0 catalogue, which maps ' +
      '27001:2022 Annex A instead, so no edition is claimed as current.',
  },
  secure_controls_framework: {
    display_name: 'Secure Controls Framework',
    status: 'rejected',
    permits: null,
    identifier_source: null,
    current_version: null,
    current_version_confirmed_on: null,
    licence: 'CC BY-ND 4.0',
    source: 'https://securecontrolsframework.com/',
    attribution: null,
    rationale:
      'The no-derivatives term explicitly covers using AI to generate derivative content from ' +
      'it absent a paid Licensed Content Provider licence — which is precisely how this ' +
      'catalogue is produced. **Recorded as a rejection rather than an omission:** a paid ' +
      'commercial licence remains a viable later shortcut to its 200-plus framework mappings, ' +
      'and that should be a decision somebody takes rather than a door that quietly closed.',
  },
  cis_controls: {
    display_name: 'CIS Critical Security Controls',
    status: 'rejected',
    permits: null,
    identifier_source: null,
    current_version: null,
    current_version_confirmed_on: null,
    licence: 'CC BY-NC-ND 4.0',
    source: 'https://www.cisecurity.org/controls',
    attribution: null,
    rationale:
      'Non-commercial only, and commercial use requires CIS approval. Zevik is a commercial ' +
      'product, so the licence does not permit it without a separate agreement.',
  },
};

/** Frameworks a mapping row may name at all. */
export function permittedFrameworks(): FrameworkId[] {
  return (Object.keys(FRAMEWORK_POLICY) as FrameworkId[]).filter(
    (id) => FRAMEWORK_POLICY[id].status === 'permitted',
  );
}

export function frameworkPermits(id: FrameworkId, level: FrameworkPermits): boolean {
  const policy = FRAMEWORK_POLICY[id];
  if (policy.status !== 'permitted' || policy.permits === null) return false;
  const ladder: FrameworkPermits[] = ['identifier', 'identifier_and_title', 'full_text'];
  return ladder.indexOf(policy.permits) >= ladder.indexOf(level);
}

/**
 * The prefix a QRS-side machine key must carry for a given framework.
 *
 * Derived from the framework id rather than declared per framework, so adding a
 * second `qrs_machine_key` framework cannot forget to bring its prefix — and so
 * there is one rule rather than a table nobody checks. `cyber_essentials` gives
 * `ce.`, which is what the catalogue writes.
 */
export function machineKeyPrefix(id: FrameworkId): string {
  const initials = id
    .split('_')
    .map((word) => word[0] ?? '')
    .join('');
  return `${initials}.`;
}

/**
 * How long a `current_version_confirmed_on` date stays meaningful.
 *
 * A year, because Cyber Essentials revises annually and it is the fastest-moving
 * framework in the table. A named constant rather than a literal at the
 * comparison site, for ZD-024's reason: the number carries a rationale and the
 * comparison should not have to.
 */
export const VERSION_CHECK_MAX_AGE_DAYS = 365;

export interface StaleVersionCheck {
  framework: FrameworkId;
  /** `never` where nobody has confirmed the edition at all. */
  confirmed_on: string | null;
  days_since: number | null;
}

/**
 * Permitted frameworks whose current edition nobody has confirmed recently.
 *
 * Reported alongside `supersededMappings` — a framework nobody has checked
 * cannot produce a superseded-row report anybody should believe, so the two
 * numbers are only meaningful together.
 *
 * `asOf` is a parameter rather than `Date.now()` so the function is pure and
 * testable, the same posture as the rest of this package.
 */
export function staleVersionChecks(asOf: Date): StaleVersionCheck[] {
  const stale: StaleVersionCheck[] = [];
  for (const framework of permittedFrameworks()) {
    const policy = FRAMEWORK_POLICY[framework];
    // A framework with no claimed current edition is not stale — it is
    // unclaimed, and `supersededMappings` already refuses to decide its rows.
    if (policy.current_version === null) continue;
    const confirmed = policy.current_version_confirmed_on;
    if (confirmed === null) {
      stale.push({ framework, confirmed_on: null, days_since: null });
      continue;
    }
    const days = Math.floor((asOf.getTime() - Date.parse(confirmed)) / 86_400_000);
    if (days > VERSION_CHECK_MAX_AGE_DAYS) {
      stale.push({ framework, confirmed_on: confirmed, days_since: days });
    }
  }
  return stale;
}
